import { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { cn } from '../../lib/cn.js';

interface SearchTriggerProps {
  className?: string;
}

export function SearchTrigger({ className }: SearchTriggerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const router = useRouterState();

  // Open on Cmd/Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  function handleOpen() {
    // If on landlords page, focus the main search instead
    if (router.location.pathname === '/landlords') {
      const mainSearch = document.querySelector<HTMLInputElement>('input[aria-label="Search landlords"]');
      if (mainSearch) {
        mainSearch.focus();
        return;
      }
    }
    setOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (q.trim()) {
      navigate({ to: '/landlords', search: { q: q.trim() } as any });
    }
    setOpen(false);
    setQ('');
  }

  return (
    <>
      <button
        className={cn(
          'p-2 rounded-lg text-secondary hover:text-primary-text hover:bg-hover-bg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center',
          className
        )}
        onClick={handleOpen}
        aria-label="Search (Cmd+K)"
        title="Search (Cmd+K)"
      >
        <Search className="w-4 h-4" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => { setOpen(false); setQ(''); }}
            aria-hidden="true"
          />
          {/* Modal */}
          <div
            className="fixed top-[20%] left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4"
            role="dialog"
            aria-modal="true"
            aria-label="Search landlords"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-border rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <Search className="w-4 h-4 text-secondary flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="search"
                  placeholder="Search landlords by name, phone, building…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="flex-1 text-sm text-primary-text outline-none placeholder:text-secondary bg-transparent min-h-[32px]"
                />
                <button
                  type="button"
                  onClick={() => { setOpen(false); setQ(''); }}
                  className="p-1 rounded text-secondary hover:text-primary-text"
                  aria-label="Close search"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {q.trim() && (
                <div className="border-t border-border px-4 py-3">
                  <button
                    type="submit"
                    className="w-full text-left text-sm text-primary hover:underline"
                  >
                    Search for "{q.trim()}" in Landlords
                  </button>
                </div>
              )}
              <div className="border-t border-border px-4 py-2">
                <p className="text-xs text-secondary">Press Enter to search · Esc to close</p>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
}
